const isRecord = value => value !== null && typeof value === "object" && !Array.isArray(value);

export const billingText = value => value == null ? "" : String(value);

function collection(value, label) {
    if (value == null) return [];
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        } catch {
            throw new Error(`${label} contains unreadable data. Reload or contact support.`);
        }
    }
    if (Array.isArray(value)) return value;
    // PHP collections with gaps in their numeric keys can arrive as JSON objects.
    if (isRecord(value) && Object.keys(value).every(key => /^\d+$/.test(key))) {
        return Object.values(value);
    }
    throw new Error(`${label} is not a valid list. Reload or contact support.`);
}

export function normalizeBillingEncounter(value) {
    if (!isRecord(value)) throw new Error("The billing batch contains an invalid encounter record.");
    const label = `Encounter ${value.id ?? "unknown"}`;
    const claims = collection(value.claims, `${label} claims`).map((claim, index) => {
        if (!isRecord(claim)) throw new Error(`${label} contains an invalid claim at row ${index + 1}.`);
        for (const field of ["amt", "sent_price", "approved_price"]) {
            if (claim[field] != null && !Number.isFinite(Number(claim[field]))) {
                throw new Error(`${label}, claim ${claim.id ?? index + 1} has an invalid amount.`);
            }
        }
        return {
            ...claim,
            claim: billingText(claim.claim ?? claim.item),
            item: billingText(claim.item ?? claim.claim),
            comment: billingText(claim.comment),
            qty: claim.qty ?? claim.sent_qty,
            amt: claim.amt ?? claim.sent_price,
            standard: claim.standard ?? claim.standard_price,
        };
    });
    return {
        ...value,
        name: billingText(value.name),
        enrollee_number: billingText(value.enrollee_number),
        diagnosis: billingText(value.diagnosis),
        claims,
    };
}

export function normalizeBillingCluster(value) {
    if (!isRecord(value) || !value.id) throw new Error("The server did not return a valid billing batch.");
    const match = billingText(value.month_year).trim().match(/^(\d{1,2})_(\d{4})$/);
    if (!match || Number(match[1]) < 1 || Number(match[1]) > 12 || Number(match[2]) < 1) {
        throw new Error("This billing batch has an invalid month or year. Contact support to correct its period.");
    }
    return {
        ...value,
        month_year: `${Number(match[1])}_${match[2]}`,
        encounters: collection(value.encounters, "Billing encounters").map(normalizeBillingEncounter),
    };
}

export function billingDate(value) {
    if (!value) return "Not recorded";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Invalid date" : date.toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
    });
}
